import React, { FC, useEffect } from 'react';

import {
  adminActions,
  convertToDecimals,
  fallbackText,
  getPharmagapTransaction,
  pharmagapTransaction,
  TimeFormat,
} from '@pharmaplan/common';

import { Box } from '@mui/material';

import usePharmagapTransactions from '../../../../hooks/Admin/PharmaGapTransactions/usePharmaGapTransactions';
import { useAppDispatch } from '../../../../hooks/useAppDispatch';
import { useAppSelector } from '../../../../hooks/useAppSelector';
import CustomGridLayout from '../../../common/CustomGridLayout';
import { getDayMonthDateYear, getHourMinute } from '../../../Reports/helpers';
import { setFooterShow, setTitle } from '../../../../actions/drawerActions';
import ServiceCard from '../../../SelfService/ServiceCard';
import { nameLabels } from '../../../../helpers/Constants';
import strings from '../../../../localization';
import DynamicTable from '../../../DynamicTable';

import {
  transactionStatusLabelMap,
  TransactionStatusStringMap,
} from '../helper';
import styles from './styles';

interface IPharmagapTransactionDetails {
  transactionId: string;
}

const successAction = adminActions.getPharmagapTransaction;

const PendingCancellationDetails: FC<IPharmagapTransactionDetails> = ({
  transactionId,
}) => {
  const dispatch = useAppDispatch();

  const transaction = useAppSelector(pharmagapTransaction);
  const { table, success } = usePharmagapTransactions();

  const {
    transactionType,
    amount,
    transactionDate,
    refundId,
    professionalName,
    pharmacyName,
    workshiftCode,
    availabilityCode,
    status,
    bookingDate = '',
    bookingDetails,
  } = transaction ?? {};

  const parsedDate = `${getDayMonthDateYear(transactionDate)} | ${getHourMinute(
    transactionDate,
    TimeFormat.twelveHours,
  )}`;
  const parsedBookingDate = getDayMonthDateYear(bookingDate ?? '');

  useEffect(() => {
    dispatch(getPharmagapTransaction(transactionId));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const transactionInfoConfig = [
    { key: strings.transactionId, value: transactionId },
    {
      key: strings.transactionType,
      value:
        TransactionStatusStringMap()[
          transactionType as keyof typeof TransactionStatusStringMap
        ],
    },
    {
      key: strings.formatString(
        strings.dateTimeSuffix,
        strings.transaction,
      ) as string,
      value: parsedDate,
    },
    { key: strings.amount, value: `$${convertToDecimals(amount)}` },
    {
      key: strings.status,
      value:
        transactionStatusLabelMap()[
          status as keyof typeof transactionStatusLabelMap
        ],
    },
    { key: strings.refundId, value: refundId ?? fallbackText },
    { key: strings.pharmacyName, value: pharmacyName },
  ];

  const bookingInfo = [
    {
      key: strings.formatString(
        strings.dateTimeSuffix,
        strings.booking,
      ) as string,
      value: parsedBookingDate,
    },
    { key: nameLabels().nameLabel, value: professionalName },
    { key: strings.workshiftCode, value: workshiftCode },
    { key: strings.availabilityCode, value: availabilityCode ?? fallbackText },
  ];

  const longTermBookingInfo = [
    { key: nameLabels().nameLabel, value: professionalName },
  ];

  useEffect(() => {
    const title = `${strings.transactionId}: ${transactionId}`;
    dispatch(setTitle({ title }));
    dispatch(setFooterShow(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <ServiceCard
        customContainerStyle={styles.serviceContainer}
        content={(
          <CustomGridLayout
            successAction={successAction}
            title={strings.transactionInfo}
            config={transactionInfoConfig}
          />
        )}
      />
      {professionalName && (
        <ServiceCard
          customContainerStyle={styles.serviceContainer}
          content={
            bookingDetails?.length ? (
              <>
                <CustomGridLayout
                  successAction={successAction}
                  title={strings.bookingInfo}
                  config={longTermBookingInfo}
                />
                <Box sx={styles.tableContainer}>
                  <DynamicTable
                    hidePagination
                    showHeader={false}
                    noContainerStyling
                    stickyHeader
                    table={table}
                    loadSuccess={success}
                  />
                </Box>
              </>
            ) : (
              <CustomGridLayout
                successAction={successAction}
                title={strings.bookingInfo}
                config={bookingInfo}
              />
            )
          }
        />
      )}
    </>
  );
};

export default PendingCancellationDetails;
